/**
 * Réconciliation des défis programmés d'une campagne (PUR, testé).
 *
 * Quand l'admin change les dates d'une campagne ou la cadence/niveau/matière d'un
 * slot, les `live_challenges` déjà matérialisés (status `scheduled`, à venir)
 * doivent être mis à jour pour rester cohérents avec la nouvelle planification —
 * sinon le cron `live-campaign-seed` (qui ne fait qu'AJOUTER) laisse des défis
 * obsolètes en place.
 *
 * `planCampaignReconcile` recalcule l'état DÉSIRÉ (mêmes helpers purs que le seed :
 * `slotOccurrences` × `campaignSlotLiveParams`) et le DIFFE contre l'existant :
 *  - occurrence existante qui n'est plus désirée → à SUPPRIMER ;
 *  - occurrence désirée absente → à INSÉRER ;
 *  - occurrence inchangée (même niveau/matière/instant) → CONSERVÉE telle quelle
 *    (on ne touche pas : exercice épinglé `source_exercise_id` + payload préservés).
 *
 * L'appelant ne fournit que des défis FUTURS encore `scheduled` (jamais de live /
 * terminé / passé : on ne ressuscite ni ne casse un défi déjà joué).
 */

import {
  slotOccurrences,
  campaignSlotLiveParams,
  weekStartOf,
  type FrequencyUnit,
} from '@jeraisonne/db/live';

export interface ReconcileCampaign {
  id: string;
  starts_at: string;
  ends_at: string | null;
  step_count: number;
  step_duration_ms: number;
  tolerance_ms: number;
}

export interface ReconcileSlot {
  id: string;
  level: string;
  subject: string | null;
  frequency_count: number;
  frequency_unit: FrequencyUnit;
  step_count: number | null;
  step_duration_ms: number | null;
}

export interface ExistingChallenge {
  id: string;
  level: string;
  subject: string | null;
  starts_at: string;
}

export interface ReconcileInsertRow {
  level: string;
  subject: string | null;
  starts_at: string;
  week_start: string;
  campaign_id: string;
  campaign_slot_id: string;
  step_count: number;
  step_duration_ms: number;
  tolerance_ms: number;
  status: 'scheduled';
}

export interface ReconcilePlan {
  toDelete: string[];
  toInsert: ReconcileInsertRow[];
}

/** Clé d'identité d'une occurrence (contrainte unique `(level, subject, starts_at)`). */
function keyOf(level: string, subject: string | null, startsAt: string): string {
  return `${level} ${subject ?? ''} ${startsAt}`;
}

export function planCampaignReconcile(
  campaign: ReconcileCampaign,
  slots: ReconcileSlot[],
  existing: ExistingChallenge[],
  opts: { now: number; horizonMs: number },
): ReconcilePlan {
  // État désiré : une ligne par occurrence, indexée par clé d'unicité.
  const desired = new Map<string, ReconcileInsertRow>();
  for (const slot of slots) {
    const params = campaignSlotLiveParams(campaign, slot);
    const occurrences = slotOccurrences(campaign, slot, {
      from: opts.now,
      horizonMs: opts.horizonMs,
    });
    for (const startsAt of occurrences) {
      const k = keyOf(slot.level, slot.subject, startsAt);
      // Collision improbable entre deux slots (même niveau+matière+instant) : 1er gagné.
      if (desired.has(k)) continue;
      desired.set(k, {
        level: slot.level,
        subject: slot.subject,
        starts_at: startsAt,
        week_start: weekStartOf(startsAt),
        campaign_id: campaign.id,
        campaign_slot_id: slot.id,
        step_count: params.step_count,
        step_duration_ms: params.step_duration_ms,
        tolerance_ms: params.tolerance_ms,
        status: 'scheduled',
      });
    }
  }

  const existingKeys = new Set(existing.map((e) => keyOf(e.level, e.subject, e.starts_at)));

  // À supprimer : défis programmés existants qui ne correspondent plus à une occurrence.
  const toDelete = existing
    .filter((e) => !desired.has(keyOf(e.level, e.subject, e.starts_at)))
    .map((e) => e.id);

  // À insérer : occurrences désirées encore absentes (les présentes sont conservées).
  const toInsert: ReconcileInsertRow[] = [];
  for (const [k, row] of desired) {
    if (!existingKeys.has(k)) toInsert.push(row);
  }

  return { toDelete, toInsert };
}

// ── Synchronisation des PARAMÈTRES de jeu (durée/nb d'étapes/tolérance) ────────
// `planCampaignReconcile` ne touche QUE la planification (dates) : un défi
// programmé dont l'instant n'a pas changé est CONSERVÉ tel quel. Donc changer la
// durée par question d'une campagne (`step_duration_ms`) ne se propagerait pas
// aux défis déjà matérialisés. Ce plan met à jour EN PLACE (id conservé →
// exercice épinglé + payload intacts ; la durée est lue sur la ligne défi, pas
// dans le payload) les défis à venir dont les paramètres résolus ont bougé.

export interface ParamSyncChallenge {
  id: string;
  campaign_slot_id: string | null;
  step_count: number;
  step_duration_ms: number;
  tolerance_ms: number;
}

export interface ParamSyncUpdate {
  id: string;
  step_count: number;
  step_duration_ms: number;
  tolerance_ms: number;
}

/**
 * Pour chaque défi à venir, recalcule ses paramètres désirés (slot override ??
 * défaut campagne, via `campaignSlotLiveParams`) et renvoie ceux qui diffèrent
 * de l'état stocké — à appliquer par un simple `update` (id conservé).
 */
export function planCampaignParamSync(
  campaign: ReconcileCampaign,
  slots: ReconcileSlot[],
  existing: ParamSyncChallenge[],
): ParamSyncUpdate[] {
  const slotById = new Map(slots.map((s) => [s.id, s]));
  const out: ParamSyncUpdate[] = [];
  for (const ch of existing) {
    const slot = ch.campaign_slot_id ? slotById.get(ch.campaign_slot_id) : undefined;
    const params = slot
      ? campaignSlotLiveParams(campaign, slot)
      : {
          step_count: campaign.step_count,
          step_duration_ms: campaign.step_duration_ms,
          tolerance_ms: campaign.tolerance_ms,
        };
    if (
      ch.step_count !== params.step_count ||
      ch.step_duration_ms !== params.step_duration_ms ||
      ch.tolerance_ms !== params.tolerance_ms
    ) {
      out.push({
        id: ch.id,
        step_count: params.step_count,
        step_duration_ms: params.step_duration_ms,
        tolerance_ms: params.tolerance_ms,
      });
    }
  }
  return out;
}

// ── Heure commune des défis (décalage EN PLACE) ───────────────────────────────
// Décaler l'heure d'une campagne ne doit PAS supprimer/recréer les défis (ça
// perdrait les exercices épinglés et les payloads déjà construits) : on garde la
// DATE locale de chaque défi et on remplace juste l'heure locale.

/** HH:MM (heure LOCALE du navigateur) d'un instant ISO, pour un `<input type="time">`. */
export function localTimeOfDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Remplace l'heure LOCALE (HH:MM) d'un instant ISO en conservant sa date locale → ISO UTC. */
export function applyLocalTimeOfDay(iso: string, hour: number, minute: number): string {
  const d = new Date(iso);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

/** Parse une valeur `<input type="time">` « HH:MM » → `{hour, minute}` ou null. */
export function parseTimeOfDay(value: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

export interface TimeShiftChallenge {
  id: string;
  starts_at: string;
}

export interface TimeShiftUpdate {
  id: string;
  starts_at: string;
  week_start: string;
}

/**
 * Pour chaque défi, calcule sa nouvelle `starts_at` (même date locale, heure
 * = `hour:minute`) et le `week_start` recalculé. Décalage EN PLACE : l'id est
 * conservé, donc l'exercice épinglé et le payload du défi restent intacts.
 */
export function planCampaignTimeShift(
  existing: TimeShiftChallenge[],
  hour: number,
  minute: number,
): TimeShiftUpdate[] {
  return existing.map((ch) => {
    const startsAt = applyLocalTimeOfDay(ch.starts_at, hour, minute);
    return { id: ch.id, starts_at: startsAt, week_start: weekStartOf(startsAt) };
  });
}
